// Fixture-catalogue GLB loader — DECISION-022 Phase 2 (Runtime-side).
//
// boot.json.fixtureCatalogue maps a fixture-kind to a CDN-resolved
// geometryAssetKey. This loader fetches + caches the catalogue GLB once
// per (kind, librarySha) so multiple anchors of the same kind share one
// network fetch.
//
// Resolver semantics (mirror of _asset-url-resolver.js):
//   1. If catalogue[kind].geometryAssetKey present AND VITE_SCENE_CDN_URL
//      set → load from `${VITE_SCENE_CDN_URL}/${geometryAssetKey}`.
//   2. Else → return null. Caller (fixture builder) falls back to the
//      shell-baked `Shell_<Kind>_Body_NN` named-node lookup.
//
// The cache is keyed by the full geometryAssetKey so when Content ships
// a new catalogue release with a fresh librarySha, the next boot picks
// up the new path automatically (versioned, immutable CDN).

// Vite-substitution gotcha (see _asset-url-resolver.js): MUST use the
// exact `import.meta.env.VITE_SCENE_CDN_URL` pattern with no optional
// chaining or destructuring; otherwise the static replacement skips and
// the bundle silently degrades to null in the browser.
const CDN_BASE = (() => {
	try {
		const raw = import.meta.env.VITE_SCENE_CDN_URL;
		if (typeof raw === "string" && raw.length > 0) return raw.replace(/\/$/, "");
	} catch { /* not under Vite — fall through */ }
	return null;
})();

// Per-key cache. Value is a Promise<{ scene, gltf }> while loading and
// the resolved object once settled. Shared across all anchors and across
// scene swaps within a session (catalogue is shop-agnostic).
const CACHE = new Map();

/**
 * Resolve the catalogue URL for a fixture-kind. Returns null when the
 * fixture-kind isn't in the catalogue (or VITE_SCENE_CDN_URL absent).
 *
 * @param {Record<string, { geometryAssetKey?: string }> | null | undefined} catalogue
 * @param {string} kind — fixture-kind, e.g. "longbox"
 * @returns {string | null}
 */
export function resolveFixtureCatalogueUrl(catalogue, kind) {
	const entry = catalogue?.[kind];
	const key = entry?.geometryAssetKey;
	if (typeof key !== "string" || key.length === 0) return null;
	if (!CDN_BASE) return null;
	return `${CDN_BASE}/${key}`;
}

/**
 * Load (and cache) the catalogue GLB for a fixture-kind. Returns the
 * gltf.scene (a THREE.Group) cloned shallow-by-reference — callers
 * MUST NOT mutate the cached scene; use `getObjectByName(...).clone()`
 * to instance individual nodes.
 *
 * @param {object} args
 * @param {Record<string, { geometryAssetKey?: string }> | null | undefined} args.catalogue
 * @param {string} args.kind
 * @param {{ loadAsync: (url: string) => Promise<any> }} args.gltfLoader — pass stage.gltfLoader
 * @returns {Promise<any | null>} the gltf object ({ scene, scenes, ... }) or null when no catalogue entry
 */
export async function loadFixtureCatalogue({ catalogue, kind, gltfLoader }) {
	const url = resolveFixtureCatalogueUrl(catalogue, kind);
	if (!url) return null;
	const cacheKey = url;
	let pending = CACHE.get(cacheKey);
	if (!pending) {
		pending = (async () => {
			try {
				const gltf = await gltfLoader.loadAsync(url);
				markCatalogueShared(gltf?.scene);
				console.log(`[fixture-catalogue] loaded ${kind} from ${url}`);
				return gltf;
			} catch (err) {
				console.warn(`[fixture-catalogue] failed to load ${kind} from ${url}:`, err);
				return null;
			}
		})();
		CACHE.set(cacheKey, pending);
	}
	return pending;
}

// 🔴 ASK-177 §6 item 4 (5g-hub 7c) — THE CACHE OWNS ITS GEOMETRY AND MATERIALS. Every fixture body is `clone(true)` of the
// cached scene, which SHARES the source's BufferGeometry and Material. The re-seat and the remove disposed their clones'
// geometry + materials — i.e. the CACHE's — and the next composed clone of that kind drew 0 pixels while standing in the
// scene graph visible with its draw call issued (measured on the plane, f8e38a2: a pinned display-pedestal's body
// vanished at the pin's re-seat; the same mesh drew 249 px with either a fresh material or a cloned geometry). A
// resource the cache owns is identified by IDENTITY here and is never disposed by an instance's teardown.
const SHARED = new WeakSet();
export function markCatalogueShared(root) {   // exported for the unit test; the loader marks every scene it caches
	root?.traverse?.((o) => {
		if (o.geometry) SHARED.add(o.geometry);
		for (const m of Array.isArray(o.material) ? o.material : (o.material ? [o.material] : [])) SHARED.add(m);
	});
}
/** True for a geometry/material owned by the catalogue cache (an instance's teardown must not dispose it). */
export function isCatalogueShared(resource) {
	return SHARED.has(resource);   // WeakSet.has(null | primitive) is false
}

// Test-helper: wipe the cache. Used by tests; not called in production.
export function _resetFixtureCatalogueCache() {
	CACHE.clear();
}
